import React, { useEffect, useState } from "react";
import { Button, Dropdown, Input, InputNumber, Menu, Message, Modal, Switch, Tooltip, Typography, Upload } from "@arco-design/web-react";
import { IconDelete, IconDown, IconFolder, IconLink, IconQuestionCircle, IconUpload } from "@arco-design/web-react/icon";
import styled from "styled-components";
import { useAuth0 } from "@auth0/auth0-react";
import { FetchApi, DeleteApi, OverwriteApi, PostApi } from "../../Api/AuthenticatedApi";
import { signupFlow } from "../signupFlow";
import FileAssetPicker from "../FileAssetPicker";
import { FOLLOWUP_DEFAULTS } from "../followupDefaults";

// The incentive (lead magnet) a campaign gives to a form filler: a file, or a
// link. A file is uploaded via multipart or chosen from Files (with the
// per-recipient use + expiry policy); a link is just a URL and button label.
// Either way it's never put in the email directly: subscribers get a personal,
// use-limited link that downloads the file or redirects to the URL. How it
// reaches a confirmed subscriber (redirect, page or follow-up email) is the
// campaign's delivery setting: Signup Settings passes its control in as
// `delivery`, shown under "How they get it".
//
// Laid out as three questions: what they get (the file or link, as one card
// with Replace and Remove), how they get it, and the limits on their link;
// then the follow-up email when that's how it's delivered.

const { Text } = Typography;

const FieldLabel = styled.div`
  font-size: 12px;
  color: var(--color-text-3);
  margin-bottom: 4px;
`;

const Section = styled.div`
  padding: 14px 0;
  & + & {
    border-top: 1px solid var(--color-border-2);
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--color-text-3);
  margin-bottom: 8px;
`;

// The reward, shown like an email attachment.
const RewardCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--color-border-2);
  border-radius: 8px;
  background: var(--color-bg-2);
`;

const FileBadge = styled.div`
  flex: none;
  width: 40px;
  height: 48px;
  border-radius: 6px;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--color-text-2);
  background: var(--color-fill-2);
`;

const extension = (name) => {
    const m = /\.([A-Za-z0-9]{1,5})$/.exec(name || "");
    return m ? m[1].toUpperCase() : "FILE";
};

function formatBytes(n) {
    if (!n) return "";
    if (n < 1024) return n + " B";
    if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
    return (n / (1024 * 1024)).toFixed(1) + " MB";
}

// The email a double opt-in campaign sends once someone confirms, when the
// download is delivered by email. Blank fields use the defaults (the download
// button goes below the message).
const DEFAULT_LINK_LABEL = "Get your reward";

// policyDirtyNow: are the limits changed from the saved ones?
const policyDirtyNow = (inc, uses, expiryHours, unlimited) => !!inc && (
    !!inc.unlimited !== unlimited || (!unlimited && (inc.default_uses !== uses || inc.expiry_hours !== expiryHours)));

const isLink = (inc) => !!(inc && inc.link_url);
const buttonLabel = (inc) => (isLink(inc) ? (inc.link_label || "").trim() || DEFAULT_LINK_LABEL : "Download " + (inc && inc.file_name ? inc.file_name : "your file"));
const validLink = (u) => /^https?:\/\/[^\s/$.?#][^\s]*$/i.test((u || "").trim());

export function FollowupEmailEditor({ campaignId, incentive, onSaved }) {
    const { getAccessTokenSilently } = useAuth0();
    const [subject, setSubject] = useState(incentive.followup_subject || "");
    const [body, setBody] = useState(incentive.followup_body || "");
    const [saving, setSaving] = useState(false);
    const dirty = subject !== (incentive.followup_subject || "") || body !== (incentive.followup_body || "");
    const defaults = FOLLOWUP_DEFAULTS[isLink(incentive) ? "link" : "file"];

    const save = () => {
        setSaving(true);
        OverwriteApi(getAccessTokenSilently, "/api/campaigns/" + campaignId + "/incentive/followup",
            (saved) => { setSaving(false); onSaved(saved); Message.success("Follow-up email saved"); },
            () => setSaving(false),
            (err) => { setSaving(false); Message.error((err && err.message) || "Failed to save the follow-up email"); },
            { subject, body });
    };

    return (
        <div data-fix-field="followup">
            <SectionTitle style={{ marginBottom: 2 }}>The follow-up email (after they confirm)</SectionTitle>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 10 }}>
                The “{buttonLabel(incentive)}” button goes below your message. You can use {"{{ .Contact.Name }}"}.
                Leave blank to use the default.
            </Text>
            <FieldLabel>Subject</FieldLabel>
            <Input value={subject} placeholder={defaults.subject} onChange={setSubject} />
            <FieldLabel style={{ marginTop: 10 }}>Message</FieldLabel>
            <Input.TextArea value={body} placeholder={defaults.body} autoSize={{ minRows: 4 }} onChange={setBody} />
            <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                <Button type="primary" loading={saving} disabled={!dirty} onClick={save}>Save follow-up email</Button>
                {dirty && <Button onClick={() => { setSubject(incentive.followup_subject || ""); setBody(incentive.followup_body || ""); }}>Discard</Button>}
            </div>
        </div>
    );
}

// The limits save with the campaign settings (their Save): limitsRef.current =
// { dirty, save() -> Promise<saved?>, discard() }, and onLimitsDirty(changed?)
// tells the settings form so its Save and Discard light up.
export default function IncentiveEditor({ campaignId, campaign, delivery, onIncentive, onGoToEmails, limitsRef, onLimitsDirty }) {
    const flow = signupFlow(campaign);
    const { getAccessTokenSilently } = useAuth0();
    const [incentive, setIncentive] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uses, setUses] = useState(5);
    const [expiryHours, setExpiryHours] = useState(48);
    // Unlimited: their link works any number of times and never expires.
    const [unlimited, setUnlimited] = useState(false);
    const [savingPolicy, setSavingPolicy] = useState(false);
    const [picking, setPicking] = useState(false);
    // "file" | "link": what the reward is (or is becoming, while the link form
    // is open); the incentive only switches when the new file/link is saved.
    const [kind, setKind] = useState("file");
    const [editingLink, setEditingLink] = useState(false);
    const fileInputRef = React.useRef(null);
    const [linkUrl, setLinkUrl] = useState("");
    const [linkLabel, setLinkLabel] = useState("");
    const [savingLink, setSavingLink] = useState(false);

    const load = () => {
        setLoading(true);
        FetchApi(
            getAccessTokenSilently,
            "/api/campaigns/" + campaignId + "/incentive",
            (data) => {
                setLoading(false);
                if (data) {
                    setIncentive(data);
                    if (data.default_uses) setUses(data.default_uses);
                    if (data.expiry_hours) setExpiryHours(data.expiry_hours);
                    setUnlimited(!!data.unlimited);
                    setKind(isLink(data) ? "link" : "file");
                    setLinkUrl(data.link_url || "");
                    setLinkLabel(data.link_label || "");
                }
            },
            () => setLoading(false),
            // 404 (no incentive yet) lands here — not an error, just "none".
            () => { setLoading(false); setIncentive(null); }
        );
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [campaignId]);

    // The settings form's Save and Discard cover the limits.
    useEffect(() => {
        if (onLimitsDirty) onLimitsDirty(policyDirtyNow(incentive, uses, expiryHours, unlimited));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [incentive, uses, expiryHours, unlimited]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => () => { if (onLimitsDirty) onLimitsDirty(false); }, []);

    // The campaign page draws the reward's part of the flow from it.
    useEffect(() => {
        if (!loading && onIncentive) onIncentive(incentive);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [incentive, loading]);

    // Multipart upload carries the file plus the use/expiry policy, so we build
    // the request by hand rather than using UploadFileApi (which only sends the
    // file). Returning false from Upload's beforeUpload stops its own XHR.
    const doUpload = async (file) => {
        setUploading(true);
        try {
            const token = await getAccessTokenSilently();
            const form = new FormData();
            form.append("uploadedFile", file);
            form.append("uses", String(uses || 5));
            form.append("expiry_hours", String(expiryHours || 48));
            form.append("unlimited", unlimited ? "true" : "false");
            const res = await fetch("/api/campaigns/" + campaignId + "/incentive", {
                method: "POST",
                headers: { Authorization: "Bearer " + token },
                body: form,
            });
            if (!res.ok) throw new Error("Upload failed (" + res.status + ")");
            const data = await res.json();
            setIncentive(data);
            setKind("file");
            setEditingLink(false);
            Message.success("Reward file saved");
        } catch (e) {
            Message.error(e.message || "Failed to upload incentive");
        } finally {
            setUploading(false);
        }
        return false;
    };

    // Use a file already in Files. It isn't copied: subscribers download the
    // file in Files, so replacing it there changes what they get.
    const chooseAsset = (name) => new Promise((resolve, reject) => {
        PostApi(getAccessTokenSilently, "/api/campaigns/" + campaignId + "/incentive/from-file",
            (data) => {
                setIncentive(data);
                setKind("file");
                setEditingLink(false);
                setPicking(false);
                Message.success(`"${data.file_name}" is now the incentive`);
                resolve();
            },
            reject,
            (err) => { Message.error((err && err.message) || "Failed to use that file"); reject(err); },
            { file_name: name, uses: uses || 5, expiry_hours: expiryHours || 48, unlimited });
    });

    const saveLink = () => {
        if (!validLink(linkUrl)) {
            Message.error("Enter a full web address starting with http:// or https://");
            return;
        }
        setSavingLink(true);
        PostApi(getAccessTokenSilently, "/api/campaigns/" + campaignId + "/incentive/link",
            (data) => { setSavingLink(false); setIncentive(data); setEditingLink(false); Message.success("Link saved"); },
            () => setSavingLink(false),
            (err) => { setSavingLink(false); Message.error((err && err.message) || "Failed to save the link"); },
            { url: linkUrl.trim(), label: linkLabel, uses: uses || 5, expiry_hours: expiryHours || 48, unlimited });
    };

    // The limits save with the campaign settings (no need to re-upload).
    // Unlimited is checked on each click, so it covers links already sent; a
    // new count/expiry only applies to links sent from now on (each link keeps
    // the limits it was created with).
    const policyDirty = policyDirtyNow(incentive, uses, expiryHours, unlimited);
    const savePolicy = () => new Promise((resolve) => {
        setSavingPolicy(true);
        OverwriteApi(getAccessTokenSilently, "/api/campaigns/" + campaignId + "/incentive/policy",
            (data) => { setSavingPolicy(false); setIncentive(data); resolve(true); },
            () => { setSavingPolicy(false); resolve(false); },
            (err) => { setSavingPolicy(false); Message.error((err && err.message) || "Failed to save the limits"); resolve(false); },
            { uses: uses || 5, expiry_hours: expiryHours || 48, unlimited });
    });
    const discardPolicy = () => {
        if (!incentive) return;
        if (incentive.default_uses) setUses(incentive.default_uses);
        if (incentive.expiry_hours) setExpiryHours(incentive.expiry_hours);
        setUnlimited(!!incentive.unlimited);
    };
    if (limitsRef) limitsRef.current = { dirty: policyDirty, save: savePolicy, discard: discardPolicy };

    const remove = () => {
        Modal.confirm({
            title: isLink(incentive) ? "Remove the reward link?" : "Remove the reward file?",
            content: "New signups will stop receiving it. Links already sent stop working.",
            okText: "Remove",
            okButtonProps: { status: "danger" },
            onOk: () => {
                DeleteApi(
                    getAccessTokenSilently,
                    "/api/campaigns/" + campaignId + "/incentive",
                    () => { setIncentive(null); setKind("file"); setEditingLink(false); Message.success("Reward removed"); },
                    () => {},
                    () => Message.error("Failed to remove incentive")
                );
            },
        });
    };

    if (loading) {
        return <Text type="secondary">Loading…</Text>;
    }

    const usesWord = kind === "link" || isLink(incentive) ? "click" : "download";
    const openUpload = () => fileInputRef.current && fileInputRef.current.click();
    const onUploadInput = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = ""; // let the same file be picked again
        if (file) doUpload(file);
    };
    const startLink = () => { setKind("link"); setEditingLink(true); };
    const onReplace = (key) => {
        if (key === "files") setPicking(true);
        if (key === "upload") openUpload();
        if (key === "link") startLink();
    };

    // The link form: new link, a switch from a file, or editing the link.
    const linkForm = (
        <div style={{ marginTop: 12, padding: "12px 14px", border: "1px solid var(--color-border-2)", borderRadius: 6, background: "var(--color-bg-2)" }}>
            <FieldLabel>Where the link goes</FieldLabel>
            <Input
                value={linkUrl}
                placeholder="https://example.com/your-course"
                status={linkUrl && !validLink(linkUrl) ? "error" : undefined}
                onChange={setLinkUrl}
            />
            <FieldLabel style={{ marginTop: 10 }}>Button text</FieldLabel>
            <Input value={linkLabel} placeholder={DEFAULT_LINK_LABEL} maxLength={80} onChange={setLinkLabel} />
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 6 }}>
                Each subscriber gets their own link (with the limits below) that sends them on to this address.
                {incentive && !isLink(incentive) && " Saving it replaces the file."}
            </Text>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <Button type="primary" icon={<IconLink />} loading={savingLink} onClick={saveLink}>Save link</Button>
                <Button onClick={() => {
                    setEditingLink(false);
                    setKind(isLink(incentive) ? "link" : "file");
                    setLinkUrl((incentive && incentive.link_url) || "");
                    setLinkLabel((incentive && incentive.link_label) || "");
                }}>Cancel</Button>
            </div>
        </div>
    );

    return (
        <div>
            <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={onUploadInput} />
            <FileAssetPicker
                visible={picking}
                onCancel={() => setPicking(false)}
                onSelect={chooseAsset}
                title="Choose the reward file"
                note="Subscribers download this file from Files. Replacing it there (uploading the same name) changes what they get; deleting it breaks their download links."
            />

            {/* What they get */}
            <Section style={{ paddingTop: 0 }}>
                <SectionTitle>What they get</SectionTitle>
                {incentive ? (
                    <RewardCard>
                        <FileBadge>{isLink(incentive) ? <IconLink style={{ fontSize: 18 }} /> : extension(incentive.file_name)}</FileBadge>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {isLink(incentive) ? buttonLabel(incentive) : incentive.file_name}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--color-text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {isLink(incentive)
                                    ? <a href={incentive.link_url} target="_blank" rel="noreferrer">{incentive.link_url}</a>
                                    : <>{formatBytes(incentive.size_bytes)}{incentive.asset_name && " · in Files"}</>}
                            </div>
                            {incentive.asset_missing && (
                                <Text type="error" style={{ fontSize: 12, display: "block" }}>
                                    This file was deleted from Files, so download links don't work. Choose another file, or upload one with the same name in Files.
                                </Text>
                            )}
                        </div>
                        {uploading && <Text type="secondary" style={{ fontSize: 12 }}>Uploading…</Text>}
                        <Dropdown
                            trigger="click"
                            position="br"
                            droplist={
                                <Menu onClickMenuItem={onReplace}>
                                    {isLink(incentive) && <Menu.Item key="link">Edit the link</Menu.Item>}
                                    <Menu.Item key="files">{isLink(incentive) ? "Use a file from Files" : "Choose from Files"}</Menu.Item>
                                    <Menu.Item key="upload">Upload a file</Menu.Item>
                                    {!isLink(incentive) && <Menu.Item key="link">Use a link instead</Menu.Item>}
                                </Menu>
                            }
                        >
                            <Button size="small" disabled={uploading}>{isLink(incentive) ? "Change" : "Replace"} <IconDown /></Button>
                        </Dropdown>
                        <Tooltip content="Remove the reward">
                            <Button size="small" type="text" status="danger" icon={<IconDelete />} onClick={remove} aria-label="Remove the reward" />
                        </Tooltip>
                    </RewardCard>
                ) : (
                    <>
                        <Upload drag multiple={false} showUploadList={false} beforeUpload={doUpload} disabled={uploading}>
                            <div style={{ padding: "18px 12px", textAlign: "center" }}>
                                <IconUpload style={{ fontSize: 20 }} />
                                <div>{uploading ? "Uploading…" : "Drop a file here, or click to upload"}</div>
                                <Text type="secondary" style={{ fontSize: 12 }}>It goes into Files too.</Text>
                            </div>
                        </Upload>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>or</Text>
                            <Button size="small" icon={<IconFolder />} onClick={() => setPicking(true)}>Choose from Files</Button>
                            <Button size="small" icon={<IconLink />} onClick={startLink}>Give a link instead</Button>
                        </div>
                    </>
                )}
                {editingLink && linkForm}
            </Section>

            {/* How they get it */}
            <Section>
                <SectionTitle>How they get it</SectionTitle>
                {/* Double opt-in: the "After they click Confirm" choice (it sums
                    itself up); single opt-in: the signup email carries it. */}
                {delivery || (flow.incentive && <Text type="secondary" style={{ fontSize: 12, display: "block" }}>{flow.incentive}</Text>)}
            </Section>

            {/* Limits */}
            <Section data-fix-field="reward_limits">
                <SectionTitle>
                    Limits
                    <Tooltip content={`Allow a few ${usesWord}s, not 1: some inboxes scan links before the subscriber opens them, and each scan counts.`}>
                        <IconQuestionCircle style={{ marginLeft: 6, cursor: "help" }} />
                    </Tooltip>
                </SectionTitle>
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, lineHeight: "32px" }}>
                    {unlimited ? (
                        <Text>Their link works any number of times and never expires.</Text>
                    ) : (
                        <>
                            <Text>Each person's link works for</Text>
                            <InputNumber size="small" min={1} max={1000} value={uses} onChange={setUses} style={{ width: 84 }} aria-label={`${usesWord}s allowed`} />
                            <Text>{usesWord}{uses === 1 ? "" : "s"} within</Text>
                            <InputNumber size="small" min={1} max={8760} value={expiryHours} onChange={setExpiryHours} style={{ width: 90 }} aria-label="hours before it expires" />
                            <Text>hour{expiryHours === 1 ? "" : "s"}.</Text>
                        </>
                    )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                    <Switch size="small" checked={unlimited} onChange={setUnlimited} />
                    <Text>Unlimited</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>no {usesWord} limit, never expires</Text>
                </div>
                {policyDirty && (
                    <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 10 }}>
                        {savingPolicy ? "Saving…" : "Saved with the campaign settings. "}
                        {!savingPolicy && (unlimited !== !!incentive.unlimited
                            ? (unlimited ? "It lifts the limits on links already sent, too." : "Links already sent go back to the limits they were sent with.")
                            : "New limits apply to emails sent from now on.")}
                    </Text>
                )}
            </Section>

            {flow.followup && (
                <Section>
                    <SectionTitle style={{ marginBottom: 2 }}>The follow-up email (after they confirm)</SectionTitle>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        It's set up with the campaign's other emails, under Emails: the built-in one (your wording) or your own design.
                    </Text>
                    {onGoToEmails && (
                        <div><Button size="small" type="text" style={{ padding: 0 }} onClick={onGoToEmails}>Go to the follow-up email →</Button></div>
                    )}
                </Section>
            )}
        </div>
    );
}
